/**
 * Helpers for the STORE fulfilment (ship / deliver) and return flows (SDC WS7).
 *
 * The backend owns every rule and every date: this file only reads the `fulfilment` block it
 * sends, decides which buttons to offer, and turns its error codes into localized copy. It does
 * no business-day or window arithmetic.
 */

/**
 * Fulfilment is only meaningful for a STORE order that is live: PAID, or COMPLETED (payout
 * release may complete a store order and it must keep showing how it was fulfilled). A cancelled
 * or refunded order keeps its last fulfilment row (still AWAITING_SHIPMENT if cancelled before
 * shipping), which must not be shown next to a "Cancelled" status.
 */
export function showsFulfilment(order) {
  return order?.orderType === 'STORE' && (order.status === 'PAID' || order.status === 'COMPLETED');
}

/** The fulfilment status of a displayable STORE order, or null (SERVICE or cancelled orders). */
export function fulfilmentStatusOf(order) {
  if (!showsFulfilment(order)) return null;
  return order.fulfilment?.status ?? null;
}

/**
 * Shipping is offered on a paid store order that has not shipped. "Paid" mirrors the backend
 * FULFILMENT_ORDER_STATUSES: PAID and COMPLETED (a payout force-released at the gateway hold
 * deadline completes the order before it is delivered). An order PAID before the fulfilment table
 * existed carries `fulfilment: null` and the server creates its row on the first ship, so null
 * counts as "awaiting shipment" - but only while the order is PAID. A COMPLETED order with no
 * fulfilment block is a pre-fulfilment order already handled through the old flow: it needs an
 * explicit AWAITING_SHIPMENT block to be shippable.
 */
export function canMarkShipped(order) {
  if (!showsFulfilment(order)) return false;
  const status = order.fulfilment?.status;
  if (status === undefined || status === null) return order.status === 'PAID';
  return status === 'AWAITING_SHIPMENT';
}

/** Delivery is offered only once shipped. */
export function canMarkDelivered(order) {
  return showsFulfilment(order) && order.fulfilment?.status === 'SHIPPED';
}

/** The late flag is only meaningful while the order is still unshipped. */
export function isLateShipment(order) {
  return showsFulfilment(order) && order.fulfilment?.late === true
    && order.fulfilment?.status === 'AWAITING_SHIPMENT';
}

export function fulfilmentChipColor(status) {
  switch (status) {
    case 'DELIVERED': return 'success';
    case 'SHIPPED': return 'info';
    case 'AWAITING_SHIPMENT': return 'warning';
    default: return 'default';
  }
}

/**
 * Milliseconds left until `iso` as { expired, days, hours, minutes }. `now` is injectable so the
 * countdown is testable. A missing/invalid date reads as null (render nothing).
 */
export function timeLeft(iso, now = Date.now()) {
  if (!iso) return null;
  const target = new Date(iso).getTime();
  if (Number.isNaN(target)) return null;
  const ms = target - now;
  if (ms <= 0) return { expired: true, days: 0, hours: 0, minutes: 0 };
  const totalMinutes = Math.floor(ms / 60000);
  return {
    expired: false,
    days: Math.floor(totalMinutes / 1440),
    hours: Math.floor((totalMinutes % 1440) / 60),
    minutes: totalMinutes % 60,
  };
}

/** "2d 5h" / "5h 12m" / "12m", localized through `storeCommon.duration*`. */
export function formatTimeLeft(left, t) {
  if (!left || left.expired) return '';
  if (left.days > 0) return t('storeCommon.durationDH', { d: left.days, h: left.hours });
  if (left.hours > 0) return t('storeCommon.durationHM', { h: left.hours, m: left.minutes });
  return t('storeCommon.durationM', { m: Math.max(left.minutes, 1) });
}

/**
 * A friendly, localized message for a failed ship / deliver / approve / reject call.
 *
 *  - 400 {code}: the rule that was broken (`storeErrors.<CODE>`); an unknown code falls back to the
 *    server's own message so a new one is readable rather than silent.
 *  - 502 REFUND_REJECTED: the payment provider refused the refund; nothing moved and the request is
 *    open again.
 *  - 500 / no response with `refund: true`: the refund outcome is UNKNOWN. The same call is
 *    idempotent, so the copy tells the store it can be repeated safely.
 *  - 409 CONFLICT: on a refund (approve) another refund on this order is in progress; on anything
 *    else (reject, ship, deliver) the record was changed at the same time by someone else, so the
 *    caller should refresh and try again.
 *  - 403 / 404: not yours / gone.
 * Pass `cancel: true` for an order cancel: a refused refund then reads "the order stays paid".
 */
export function storeActionError(err, t, { refund = false, cancel = false } = {}) {
  const status = err?.response?.status;
  const data = err?.response?.data || {};
  const code = data.code;

  if (status === 502 && code === 'REFUND_REJECTED') {
    // A cancel whose refund was refused leaves the ORDER paid (there is no return request to reopen).
    return t(cancel ? 'storeErrors.REFUND_REJECTED_CANCEL' : 'storeErrors.REFUND_REJECTED');
  }
  // 409: for a refund, another refund on this order is in flight; for any other action the record
  // moved under the caller. A coded 409 (e.g. a rule) maps by its code.
  if (status === 409 && (!code || code === 'CONFLICT')) {
    return t(refund ? 'storeErrors.CONFLICT' : 'storeErrors.CONFLICT_CHANGED');
  }
  if (status === 400 || status === 409) {
    if (code) {
      return t(`storeErrors.${code}`, { defaultValue: data.message || t('storeErrors.generic') });
    }
    return data.message || t('storeErrors.generic');
  }
  if (status === 403) return t('storeErrors.forbidden');
  if (status === 404) return t('storeErrors.notFound');
  if (refund && (status === undefined || status >= 500)) return t('storeErrors.unknownOutcome');
  return t('storeErrors.generic');
}

/**
 * True when a failed refund call left the outcome UNKNOWN (a 5xx other than the coded gateway
 * refusal, or no response at all). The
 * request is already APPROVED server-side and a repeat settles the ORIGINAL decision, so the form
 * must not offer edits that the retry would silently ignore.
 */
export function isUnknownRefundOutcome(err) {
  const status = err?.response?.status;
  if (status === 502 && err?.response?.data?.code === 'REFUND_REJECTED') return false; // definite refusal
  return status === undefined || status === null || status >= 500;
}

/**
 * True when a failed cancel means the order moved under the caller (it shipped, or it is already
 * cancelled / completed), so the list should be reloaded to show the real state and drop Cancel.
 */
export function isStaleCancelError(err) {
  const code = err?.response?.data?.code;
  return err?.response?.status === 400
    && (code === 'CANCEL_AFTER_SHIPPING' || code === 'ORDER_NOT_CANCELLABLE');
}

/**
 * True when a failed refund call means the request or order moved under the caller (another refund
 * in flight, the amount no longer fits, the gateway refused and re-opened it), so the list should
 * be reloaded to show the real state.
 */
export function isStaleRefundError(err) {
  const status = err?.response?.status;
  const code = err?.response?.data?.code;
  return status === 409
    || code === 'RETURN_REFUND_EXCEEDS_REMAINING'
    || code === 'INVALID_REFUND_AMOUNT'
    || (status === 502 && code === 'REFUND_REJECTED');
}
